// Rotas falsas de Workflow (inbox, tarefas, aprovações, exceções, métricas, políticas/regras — M1 §5/§6).
// Comportamento simplificado (sem RLS real, sem SoD completo): o bastante para exercitar a SPA de ponta a ponta
// contra `lib/fake-api` antes do contrato existir (M1 §8 "Before the contract exists").
import type { MeResponse } from '@giyus/api-client';

import type {
  WorkflowApprovalDetail,
  WorkflowExceptionDetail,
  WorkflowInboxCountersResponse,
  WorkflowInboxItem,
  WorkflowInboxItemsResponse,
  WorkflowMetricsRow,
  WorkflowTaskDetail,
  WorkItemKind,
} from '../../modules/workflow/api/contract.preview';
import { FakeApiError, jsonResponse, readJsonBody, requireReason } from './http';
import {
  type FakeApproval,
  type FakeException,
  type FakeTask,
  type FakeWorkflowState,
  callerHasRole,
  isApprovalVisible,
  isTaskOrExceptionVisible,
  nextId,
  ownerOf,
  recordHistory,
  scopeOf,
} from './workflowStore';

interface ScopeMatch {
  readonly tenantId: string | null;
  readonly studySiteId: string | null;
  readonly path: string;
}

export function matchScope(
  segments: readonly string[],
): { scope: ScopeMatch; rest: string[] } | null {
  if (segments[0] === 'network') {
    return {
      scope: { tenantId: null, studySiteId: null, path: 'network' },
      rest: [...segments.slice(1)],
    };
  }
  if (segments[0] === 'tenants' && segments[1]) {
    const tenantId = segments[1];
    if (segments[2] === 'studies' && segments[3]) {
      return {
        scope: {
          tenantId,
          studySiteId: segments[3],
          path: `tenants/${tenantId}/studies/${segments[3]}`,
        },
        rest: [...segments.slice(4)],
      };
    }
    return {
      scope: { tenantId, studySiteId: null, path: `tenants/${tenantId}` },
      rest: [...segments.slice(2)],
    };
  }
  return null;
}

function isOverdue(dueAt: string | null): boolean {
  return dueAt !== null && new Date(dueAt).getTime() < Date.now();
}

/** Ações do botoeira (`WorkItemActionBar`). O comentário não é uma ação de estado — o formulário de comentário
 * em `TaskDetail` fica sempre visível para quem enxerga o item, então não entra em `allowedActions`. */
function taskAllowedActions(task: FakeTask): string[] {
  const canClaim =
    task.assigneeUserId === null && task.status !== 'Done' && task.status !== 'Cancelled';
  const actions: string[] = [];
  if (canClaim) {
    actions.push('claim');
  }
  if (task.status === 'Open' || task.status === 'Blocked') {
    actions.push('start');
  }
  if (task.status === 'Open' || task.status === 'InProgress' || task.status === 'Blocked') {
    actions.push('complete', 'block', 'cancel');
  }
  return [...new Set(actions)];
}

function toTaskItem(task: FakeTask, me: MeResponse): WorkflowInboxItem {
  return {
    kind: 'task',
    id: task.id,
    typeCode: task.typeCode,
    subject: task.subject,
    status: task.status,
    priority: task.priority,
    severity: null,
    dueAt: task.dueAt,
    overdue: isOverdue(task.dueAt) && task.status !== 'Done' && task.status !== 'Cancelled',
    createdAt: task.createdAt,
    scope: scopeOf(task),
    owner: ownerOf(task),
    assignedToMe: task.assigneeUserId === me.user.id,
    claimable:
      task.assigneeUserId === null && task.status !== 'Done' && task.status !== 'Cancelled',
    source: task.source,
    consequence: null,
    approval: null,
    allowedActions: taskAllowedActions(task),
  };
}

function toTaskDetail(task: FakeTask, me: MeResponse): WorkflowTaskDetail {
  return {
    ...toTaskItem(task, me),
    kind: 'task',
    description: task.description,
    blockedReason: task.blockedReason,
    assigneeUserId: task.assigneeUserId,
    assigneeName: task.assigneeName,
    requiresEvidence: task.requiresEvidence,
    evidencePresent: task.evidencePresent,
    checklist: task.checklist,
    comments: task.comments,
    history: task.history,
    etag: `W/"${task.id}-${String(task.history.length)}-${task.status}"`,
  };
}

function exceptionAllowedActions(exception: FakeException): string[] {
  if (exception.status === 'Resolved' || exception.status === 'Dismissed') {
    return [];
  }
  const actions = ['resolve', 'dismiss'];
  if (exception.assigneeUserId === null) {
    actions.push('assign');
  }
  return actions;
}

function toExceptionItem(exception: FakeException, me: MeResponse): WorkflowInboxItem {
  return {
    kind: 'exception',
    id: exception.id,
    typeCode: exception.typeCode,
    subject: null,
    status: exception.status,
    priority: null,
    severity: exception.severity,
    dueAt: exception.slaDueAt,
    overdue:
      isOverdue(exception.slaDueAt) &&
      exception.status !== 'Resolved' &&
      exception.status !== 'Dismissed',
    createdAt: exception.raisedAt,
    scope: scopeOf(exception),
    owner: ownerOf(exception),
    assignedToMe: exception.assigneeUserId === me.user.id,
    claimable: exception.assigneeUserId === null && exception.status === 'Open',
    source: exception.reference,
    consequence: exception.consequence,
    approval: null,
    allowedActions: exceptionAllowedActions(exception),
  };
}

function toExceptionDetail(exception: FakeException, me: MeResponse): WorkflowExceptionDetail {
  return {
    ...toExceptionItem(exception, me),
    kind: 'exception',
    detail: exception.detail,
    occurrenceCount: exception.occurrenceCount,
    lastOccurredAt: exception.lastOccurredAt,
    slaDueAt: exception.slaDueAt,
    history: exception.history,
    etag: `W/"${exception.id}-${String(exception.history.length)}-${exception.status}"`,
  };
}

function approvalAllowedActions(approval: FakeApproval, me: MeResponse): string[] {
  if (approval.status !== 'Pending' || !isApprovalVisible(me, approval)) {
    return [];
  }
  if (approval.requestedByUserId === me.user.id) {
    return [];
  }
  return ['decide'];
}

function toApprovalItem(approval: FakeApproval, me: MeResponse): WorkflowInboxItem {
  const step = approval.steps.find((s) => s.order === approval.currentStep);
  return {
    kind: 'approval',
    id: approval.id,
    typeCode: approval.actType,
    subject: approval.objectSummary,
    status: approval.status,
    priority: null,
    severity: null,
    dueAt: approval.dueAt,
    overdue: isOverdue(approval.dueAt) && approval.status === 'Pending',
    createdAt: approval.requestedAt,
    scope: scopeOf(approval),
    owner: { kind: 'Role', userId: null, roleCode: step?.approverRoleCodes[0] ?? null },
    assignedToMe: false,
    claimable: false,
    source: null,
    consequence: null,
    approval: {
      actType: approval.actType,
      objectType: approval.objectType,
      objectId: approval.objectId,
      objectVersion: approval.objectVersion,
      objectSummary: approval.objectSummary,
      objectContentHash: approval.objectContentHash,
      step: approval.currentStep,
      requiresSignature: approval.requiresSignature,
      allowedMeanings: ['Approved', 'Reviewed'],
    },
    allowedActions: approvalAllowedActions(approval, me),
  };
}

function toApprovalDetail(approval: FakeApproval, me: MeResponse): WorkflowApprovalDetail {
  const item = toApprovalItem(approval, me);
  return {
    ...item,
    kind: 'approval',
    approval: item.approval ?? {
      actType: approval.actType,
      objectType: approval.objectType,
      objectId: approval.objectId,
      objectVersion: approval.objectVersion,
      objectSummary: approval.objectSummary,
      objectContentHash: approval.objectContentHash,
      step: approval.currentStep,
      requiresSignature: approval.requiresSignature,
      allowedMeanings: ['Approved', 'Reviewed'],
    },
    decisions: approval.decisions.map((d) => ({
      id: d.id,
      step: d.step,
      deciderName: d.deciderName,
      decision: d.decision,
      meaning: d.meaning as WorkflowApprovalDetail['decisions'][number]['meaning'],
      comment: d.comment,
      decidedAt: d.decidedAt,
    })),
    etag: `W/"${approval.id}-${String(approval.decisions.length)}-${approval.status}"`,
  };
}

function inScope(tenantId: string | null, scope: ScopeMatch): boolean {
  if (scope.path === 'network') {
    return tenantId === null;
  }
  return tenantId === scope.tenantId;
}

function findAllVisibleItems(state: FakeWorkflowState, me: MeResponse): WorkflowInboxItem[] {
  const tasks = state.tasks
    .filter((t) => isTaskOrExceptionVisible(me, t))
    .map((t) => toTaskItem(t, me));
  const exceptions = state.exceptions
    .filter((e) => isTaskOrExceptionVisible(me, e))
    .map((e) => toExceptionItem(e, me));
  const approvals = state.approvals
    .filter((a) => isApprovalVisible(me, a))
    .map((a) => toApprovalItem(a, me));
  return [...tasks, ...exceptions, ...approvals];
}

function sortInbox(items: WorkflowInboxItem[]): WorkflowInboxItem[] {
  return [...items].sort((a, b) => {
    if (a.overdue !== b.overdue) {
      return a.overdue ? -1 : 1;
    }
    const dueA = a.dueAt ? new Date(a.dueAt).getTime() : Number.POSITIVE_INFINITY;
    const dueB = b.dueAt ? new Date(b.dueAt).getTime() : Number.POSITIVE_INFINITY;
    if (dueA !== dueB) {
      return dueA - dueB;
    }
    return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
  });
}

function buildCounters(items: readonly WorkflowInboxItem[]): WorkflowInboxCountersResponse {
  const byScope = new Map<string, number>();
  for (const item of items) {
    const key = item.scope.tenantId ?? '__network__';
    byScope.set(key, (byScope.get(key) ?? 0) + 1);
  }
  return {
    total: items.length,
    byKind: {
      task: items.filter((i) => i.kind === 'task').length,
      approval: items.filter((i) => i.kind === 'approval').length,
      exception: items.filter((i) => i.kind === 'exception').length,
    },
    overdue: items.filter((i) => i.overdue).length,
    byScope: [...byScope.entries()].map(([tenantId, count]) => ({
      tenantId: tenantId === '__network__' ? null : tenantId,
      count,
    })),
  };
}

function buildMetrics(state: FakeWorkflowState, scope: ScopeMatch): WorkflowMetricsRow[] {
  const rows = new Map<string, WorkflowMetricsRow>();
  const bump = (kind: WorkItemKind, typeCode: string, overdue: boolean): void => {
    const key = `${kind}:${typeCode}`;
    const existing = rows.get(key) ?? {
      kind,
      typeCode,
      open: 0,
      overdue: 0,
      aging: { lt1d: 0, d1to3: 0, d3to7: 0, gt7d: 0 },
      resolvedInPeriod: 0,
      meanResolutionHours: null,
    };
    rows.set(key, {
      ...existing,
      open: existing.open + 1,
      overdue: existing.overdue + (overdue ? 1 : 0),
    });
  };
  for (const task of state.tasks) {
    if (!inScope(task.tenantId, scope) || task.status === 'Done' || task.status === 'Cancelled') {
      continue;
    }
    bump('task', task.typeCode, isOverdue(task.dueAt));
  }
  for (const exception of state.exceptions) {
    if (
      !inScope(exception.tenantId, scope) ||
      exception.status === 'Resolved' ||
      exception.status === 'Dismissed'
    ) {
      continue;
    }
    bump('exception', exception.typeCode, isOverdue(exception.slaDueAt));
  }
  for (const approval of state.approvals) {
    if (!inScope(approval.tenantId, scope) || approval.status !== 'Pending') {
      continue;
    }
    bump('approval', approval.actType, isOverdue(approval.dueAt));
  }
  return [...rows.values()];
}

export interface WorkflowRouteContext {
  readonly state: FakeWorkflowState;
  readonly me: MeResponse;
  readonly correlationId: string | null;
}

/** Roteia uma requisição de Workflow; devolve `null` quando o caminho não pertence a este módulo. */
export async function handleWorkflowRoute(
  request: Request,
  segments: readonly string[],
  ctx: WorkflowRouteContext,
): Promise<Response | null> {
  const { state, me, correlationId } = ctx;
  const method = request.method.toUpperCase();

  try {
    if (segments[0] === 'me' && segments[1] === 'inbox') {
      const items = sortInbox(findAllVisibleItems(state, me));
      if (segments[2] === 'counters') {
        return jsonResponse(buildCounters(items));
      }
      if (segments[2] === 'items' && segments[3] && segments[4]) {
        const kind = segments[3] as WorkItemKind;
        const id = segments[4];
        const detail = findDetail(state, me, kind, id);
        return detail ? jsonResponse(detail) : problemNotFound(correlationId);
      }
      if (segments.length === 2) {
        const url = new URL(request.url);
        const kindFilter = url.searchParams.getAll('kind');
        const overdueOnly = url.searchParams.get('overdue') === 'true';
        const tenantFilter = url.searchParams.get('tenantId');
        const filtered = items.filter(
          (item) =>
            (kindFilter.length === 0 || kindFilter.includes(item.kind)) &&
            (!overdueOnly || item.overdue) &&
            (!tenantFilter || item.scope.tenantId === tenantFilter),
        );
        const response: WorkflowInboxItemsResponse = { items: filtered, nextCursor: null };
        return jsonResponse(response);
      }
    }

    const matched = matchScope(segments);
    if (!matched) {
      return null;
    }
    const { scope, rest } = matched;
    if (scope.path === 'network' && !me.networkAccess.hasAccess) {
      throw new FakeApiError(403, 'PLT_NETWORK_SCOPE_REQUIRED');
    }

    if (rest[0] === 'tasks') {
      return await handleTasks(request, method, rest.slice(1), { state, me, scope, correlationId });
    }
    if (rest[0] === 'approvals') {
      return await handleApprovals(request, method, rest.slice(1), {
        state,
        me,
        scope,
        correlationId,
      });
    }
    if (rest[0] === 'exceptions') {
      return await handleExceptions(request, method, rest.slice(1), {
        state,
        me,
        scope,
        correlationId,
      });
    }
    if (rest[0] === 'workflow' && rest[1] === 'metrics' && method === 'GET') {
      return jsonResponse({ items: buildMetrics(state, scope) });
    }
    if (
      scope.path === 'network' &&
      rest[0] === 'workflow' &&
      rest[1] === 'approval-policies' &&
      method === 'GET'
    ) {
      return jsonResponse({ items: state.policies });
    }
    if (
      scope.path === 'network' &&
      rest[0] === 'workflow' &&
      rest[1] === 'escalation-rules' &&
      method === 'GET'
    ) {
      return jsonResponse({ items: state.rules });
    }
    return null;
  } catch (error) {
    if (error instanceof FakeApiError) {
      return problemResponseFrom(error, correlationId);
    }
    throw error;
  }
}

function problemResponseFrom(error: FakeApiError, correlationId: string | null): Response {
  return new Response(
    JSON.stringify({
      status: error.status,
      errorCode: error.errorCode,
      title: error.errorCode,
      correlationId: correlationId ?? undefined,
      ...error.extra,
    }),
    { status: error.status, headers: { 'content-type': 'application/problem+json' } },
  );
}

function problemNotFound(correlationId: string | null): Response {
  return problemResponseFrom(new FakeApiError(404, 'PLT_NOT_FOUND'), correlationId);
}

function findDetail(
  state: FakeWorkflowState,
  me: MeResponse,
  kind: WorkItemKind,
  id: string,
): WorkflowTaskDetail | WorkflowApprovalDetail | WorkflowExceptionDetail | null {
  if (kind === 'task') {
    const task = state.tasks.find((t) => t.id === id);
    return task && isTaskOrExceptionVisible(me, task) ? toTaskDetail(task, me) : null;
  }
  if (kind === 'exception') {
    const exception = state.exceptions.find((e) => e.id === id);
    return exception && isTaskOrExceptionVisible(me, exception)
      ? toExceptionDetail(exception, me)
      : null;
  }
  const approval = state.approvals.find((a) => a.id === id);
  return approval && isApprovalVisible(me, approval) ? toApprovalDetail(approval, me) : null;
}

interface ScopedContext {
  readonly state: FakeWorkflowState;
  readonly me: MeResponse;
  readonly scope: ScopeMatch;
  readonly correlationId: string | null;
}

async function handleTasks(
  request: Request,
  method: string,
  rest: readonly string[],
  ctx: ScopedContext,
): Promise<Response> {
  const { state, me, scope } = ctx;
  if (rest.length === 0 && method === 'GET') {
    const items = state.tasks
      .filter((t) => inScope(t.tenantId, scope))
      .map((t) => toTaskDetail(t, me));
    return jsonResponse({ items });
  }
  const id = rest[0];
  const task = id ? state.tasks.find((t) => t.id === id) : undefined;
  if (!task || !inScope(task.tenantId, scope)) {
    return problemNotFound(ctx.correlationId);
  }
  if (rest.length === 1 && method === 'GET') {
    return jsonResponse(toTaskDetail(task, me));
  }
  if (rest[1] === 'checklist' && rest[2] && method === 'PUT') {
    const body = (await readJsonBody(request)) as { isChecked?: boolean };
    const item = task.checklist.find((c) => c.id === rest[2]);
    if (!item) {
      return problemNotFound(ctx.correlationId);
    }
    item.isChecked = body.isChecked === true;
    item.checkedAt = item.isChecked ? new Date().toISOString() : null;
    item.checkedBy = item.isChecked ? me.user.displayName : null;
    return jsonResponse(toTaskDetail(task, me));
  }
  if (rest[1] === 'comments' && method === 'POST') {
    const body = (await readJsonBody(request)) as { body?: string };
    const text = body.body?.trim();
    if (!text) {
      throw new FakeApiError(422, 'PLT_VALIDATION', {
        errors: [{ field: 'body', code: 'REQUIRED', message: 'Required.' }],
      });
    }
    task.comments.push({
      id: nextId('comment'),
      body: text,
      authorId: me.user.id,
      authorName: me.user.displayName,
      createdAt: new Date().toISOString(),
      deletedAt: null,
      deleteReason: null,
    });
    return jsonResponse(toTaskDetail(task, me), 201);
  }
  if (rest.length === 2 && method === 'POST') {
    return applyTaskAction(task, rest[1] ?? '', request, me, ctx.correlationId);
  }
  return problemNotFound(ctx.correlationId);
}

async function applyTaskAction(
  task: FakeTask,
  action: string,
  request: Request,
  me: MeResponse,
  correlationId: string | null,
): Promise<Response> {
  const body = await readJsonBody(request);
  switch (action) {
    case 'claim': {
      if (task.assigneeUserId !== null) {
        throw new FakeApiError(409, 'WF_ALREADY_CLAIMED');
      }
      if (
        task.ownerRoleCode &&
        !callerHasRole(me, task.ownerRoleCode, task.tenantId) &&
        task.ownerUserId !== me.user.id
      ) {
        throw new FakeApiError(422, 'WF_NOT_ROLE_MEMBER');
      }
      task.assigneeUserId = me.user.id;
      task.assigneeName = me.user.displayName;
      recordHistory(task.history, 'WF.TASK_CLAIMED', me);
      break;
    }
    case 'start': {
      if (task.status !== 'Open' && task.status !== 'Blocked') {
        throw new FakeApiError(409, 'WF_INVALID_TRANSITION');
      }
      if (task.assigneeUserId === null) {
        task.assigneeUserId = me.user.id;
        task.assigneeName = me.user.displayName;
      }
      task.status = 'InProgress';
      recordHistory(task.history, 'WF.TASK_STARTED', me);
      break;
    }
    case 'block': {
      const reason = requireReason(body);
      task.blockedReason = reason;
      task.status = 'Blocked';
      recordHistory(task.history, 'WF.TASK_BLOCKED', me, reason);
      break;
    }
    case 'complete': {
      if (task.status === 'Done' || task.status === 'Cancelled') {
        throw new FakeApiError(409, 'WF_INVALID_TRANSITION');
      }
      if (task.checklist.some((c) => c.isRequired && !c.isChecked)) {
        throw new FakeApiError(422, 'WF_CHECKLIST_INCOMPLETE');
      }
      if (task.requiresEvidence && !task.evidencePresent) {
        throw new FakeApiError(422, 'WF_EVIDENCE_REQUIRED');
      }
      task.status = 'Done';
      recordHistory(task.history, 'WF.TASK_COMPLETED', me);
      break;
    }
    case 'cancel': {
      const reason = requireReason(body);
      if (task.status === 'Done' || task.status === 'Cancelled') {
        throw new FakeApiError(409, 'WF_INVALID_TRANSITION');
      }
      task.status = 'Cancelled';
      recordHistory(task.history, 'WF.TASK_CANCELLED', me, reason);
      break;
    }
    default:
      return problemNotFound(correlationId);
  }
  return jsonResponse(toTaskDetail(task, me));
}

async function handleApprovals(
  request: Request,
  method: string,
  rest: readonly string[],
  ctx: ScopedContext,
): Promise<Response> {
  const { state, me, scope } = ctx;
  if (rest.length === 0 && method === 'GET') {
    const items = state.approvals
      .filter((a) => inScope(a.tenantId, scope))
      .map((a) => toApprovalDetail(a, me));
    return jsonResponse({ items });
  }
  const id = rest[0];
  const approval = id ? state.approvals.find((a) => a.id === id) : undefined;
  if (!approval || !inScope(approval.tenantId, scope)) {
    return problemNotFound(ctx.correlationId);
  }
  if (rest.length === 1 && method === 'GET') {
    return jsonResponse(toApprovalDetail(approval, me));
  }
  if (rest[1] === 'decide' && method === 'POST') {
    const body = (await readJsonBody(request)) as {
      decision?: 'Approved' | 'Rejected' | 'Returned';
      meaning?: string;
      comment?: string;
      expectedObjectContentHash?: string;
    };
    if (approval.status !== 'Pending') {
      throw new FakeApiError(409, 'WF_INVALID_TRANSITION');
    }
    if (approval.requestedByUserId === me.user.id) {
      throw new FakeApiError(422, 'AUTHZ_SOD_VIOLATION');
    }
    const step = approval.steps.find((s) => s.order === approval.currentStep);
    if (!step?.approverRoleCodes.some((role) => callerHasRole(me, role, approval.tenantId))) {
      throw new FakeApiError(422, 'WF_APPROVER_NOT_ELIGIBLE');
    }
    if (
      body.expectedObjectContentHash &&
      body.expectedObjectContentHash !== approval.objectContentHash
    ) {
      throw new FakeApiError(409, 'WF_APPROVAL_OBJECT_CHANGED');
    }
    const decision = body.decision ?? 'Approved';
    approval.decisions.push({
      id: nextId('decision'),
      step: approval.currentStep,
      deciderUserId: me.user.id,
      deciderName: me.user.displayName,
      decision,
      meaning: body.meaning ?? 'Approved',
      comment: body.comment ?? null,
      decidedAt: new Date().toISOString(),
    });
    if (decision === 'Approved') {
      approval.status = 'Approved';
      approval.closedAt = new Date().toISOString();
    } else {
      approval.status = decision;
      approval.closedAt = new Date().toISOString();
    }
    return jsonResponse(toApprovalDetail(approval, me));
  }
  return problemNotFound(ctx.correlationId);
}

async function handleExceptions(
  request: Request,
  method: string,
  rest: readonly string[],
  ctx: ScopedContext,
): Promise<Response> {
  const { state, me, scope } = ctx;
  if (rest.length === 0 && method === 'GET') {
    const items = state.exceptions
      .filter((e) => inScope(e.tenantId, scope))
      .map((e) => toExceptionDetail(e, me));
    return jsonResponse({ items });
  }
  const id = rest[0];
  const exception = id ? state.exceptions.find((e) => e.id === id) : undefined;
  if (!exception || !inScope(exception.tenantId, scope)) {
    return problemNotFound(ctx.correlationId);
  }
  if (rest.length === 1 && method === 'GET') {
    return jsonResponse(toExceptionDetail(exception, me));
  }
  if (rest.length === 2 && method === 'POST') {
    const body = await readJsonBody(request);
    switch (rest[1]) {
      case 'assign': {
        const reason = requireReason(body);
        exception.assigneeUserId = me.user.id;
        exception.assigneeName = me.user.displayName;
        exception.status = 'Assigned';
        recordHistory(exception.history, 'WF.EXCEPTION_ASSIGNED', me, reason);
        break;
      }
      case 'resolve': {
        const reason = requireReason(body);
        exception.status = 'Resolved';
        recordHistory(exception.history, 'WF.EXCEPTION_RESOLVED', me, reason);
        break;
      }
      case 'dismiss': {
        const reason = requireReason(body);
        exception.status = 'Dismissed';
        recordHistory(exception.history, 'WF.EXCEPTION_DISMISSED', me, reason);
        break;
      }
      default:
        return problemNotFound(ctx.correlationId);
    }
    return jsonResponse(toExceptionDetail(exception, me));
  }
  return problemNotFound(ctx.correlationId);
}
